import React from 'react';
import { useAuth } from '@/App';
import { LogOut, LayoutDashboard, Syringe, FileText, Settings, DollarSign, Menu, X, Bell, Calendar, Users, Shield, UserCog, Building2, Package, Database, Ticket, Monitor, Mail, Send, MessageCircle, History, ClipboardCheck, CalendarClock, ScrollText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';

const roleLabels = {
  ADMIN: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  ADMINISTRATIVO: { label: 'Administrativo', color: 'bg-blue-100 text-blue-700' },
  ENFERMERA: { label: 'Enfermera', color: 'bg-emerald-100 text-emerald-700' },
  CAJA: { label: 'Caja', color: 'bg-amber-100 text-amber-700' }
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build nav items based on user permissions
  const getNavItems = () => {
    const items = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, resource: 'dashboard' },
      { path: '/vaccinations', label: 'Vacunaciones', icon: Syringe, resource: 'vaccinations' },
      { path: '/vacunaciones/pendientes', label: 'Pendientes de aplicar', icon: ClipboardCheck, resource: 'vaccinations' },
      { path: '/reservas', label: 'Reservas', icon: CalendarClock, resource: 'reservas' },
      { path: '/pacientes', label: 'Pacientes', icon: Users, resource: 'vaccinations' },
      { path: '/historial', label: 'Historial RUT', icon: History, resource: 'vaccinations' },
      { path: '/turnos', label: 'Turnos', icon: Ticket, resource: 'vaccinations' },
      { path: '/alerts', label: 'Alertas', icon: Bell, resource: 'alerts' },
      { path: '/inventario', label: 'Inventario', icon: Package, resource: 'catalogs' },
      { path: '/daily-cash', label: 'Caja Diaria', icon: DollarSign, resource: 'daily_cash' },
      { path: '/reports', label: 'Reportes', icon: FileText, resource: 'reports' },
      { path: '/vaccine-schedules', label: 'Esquemas', icon: Calendar, resource: 'schedules' },
      { path: '/staff', label: 'Personal', icon: UserCog, resource: 'catalogs' },
      { path: '/empresas', label: 'Empresas', icon: Building2, resource: 'catalogs' },
      { path: '/display-admin', label: 'Pantalla', icon: Monitor, resource: 'catalogs' },
      { path: '/catalogs', label: 'Catálogos', icon: Settings, resource: 'catalogs' },
      { path: '/actividad', label: 'Registro de Actividad', icon: ScrollText, resource: 'actividad' },
    ];

    // Add admin pages
    if (user?.role === 'ADMIN') {
      items.push({ path: '/users', label: 'Usuarios', icon: Users, resource: 'users' });
      items.push({ path: '/roles', label: 'Roles', icon: Shield, resource: 'roles' });
      items.push({ path: '/kiosko-config', label: 'Quioscos', icon: Ticket, resource: 'roles' });
      items.push({ path: '/backup', label: 'Backup', icon: Database, resource: 'roles' });
      items.push({ path: '/email-config', label: 'Correo', icon: Mail, resource: 'roles' });
      items.push({ path: '/whatsapp-config', label: 'WhatsApp', icon: MessageCircle, resource: 'roles' });
      items.push({ path: '/notifications', label: 'Notificaciones', icon: Send, resource: 'notifications' });
    }

    // Filter based on permissions
    return items.filter(item => {
      const permissions = user?.permissions?.[item.resource];
      return permissions && permissions.length > 0;
    });
  };

  const navItems = getNavItems();

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="main-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 md:p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                <Syringe className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-slate-900 heading truncate">Vacu-Track</h1>
                <p className="text-xs text-slate-500">Sistema de Gestión</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-2 md:p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-3 md:p-4 border-t border-slate-200">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <Avatar className="h-9 w-9 md:h-10 md:w-10 flex-shrink-0">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            {user?.role && (
              <Badge className={`${roleLabels[user.role]?.color || 'bg-slate-100'} mb-2 text-xs`}>
                {roleLabels[user.role]?.label || user.role}
              </Badge>
            )}
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2 text-slate-600 hover:text-red-600 hover:border-red-200 text-sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="truncate">Cerrar Sesión</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Mobile header */}
        <header className="md:hidden bg-white border-b border-slate-200 p-3 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100 -ml-1"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Syringe className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900 heading text-sm">Vacu-Track</span>
          </div>
          <div className="w-10" /> {/* Spacer */}
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
